// WS-75 materials: composable lighting hooks for node materials (snow tint, snow sparkle, specular cap, the cast light
// of lazy/lightcast.js).
//
// A hook runs inside the material's own lighting model, so it sees the light the material received, split as three
// splits it (reflectedLight: directDiffuse, directSpecular, indirectDiffuse, indirectSpecular), and can add to it.
// Several systems can hook the same material; each adds its own hook (by key, once), none overwrites a node slot.
//
//   addLightHook(material, { key, lights(ctx), finish(ctx) })
//     key       unique per kind of hook (also part of the material's program cache key)
//     lights    runs after every light (direct) and the environment / hemisphere (indirect, AO applied) went into
//               ctx.reflectedLight, before they are summed. THE PLACE FOR CAST LIGHT (lazy/lightcast.js): add the
//               resort's window / lamp light to ctx.reflectedLight.directDiffuse (or indirectDiffuse x
//               ctx.ambientOcclusion), and caps on the direct light.
//     finish    runs after the sum (ctx.outgoingLight = diffuse + specular; clearcoat / sheen already in), before the
//               emissive part is added: the place for looks on the lit result (snow tint, sparkle).
//   ctx = { builder, material, model, reflectedLight, outgoingLight, ambientOcclusion, diffuseColor }
//     All TSL nodes: read them, or .assign / .addAssign / .mulAssign to change them.
//
// Hooks belong to the material object: material.clone() does not carry them.
// Hooks are only built while the scene has lights; a material with lights = false never runs them.
import * as THREE from 'three/webgpu';
import { diffuseColor, If, materialReference, max, min, vec3 } from 'three/tsl';
import { usable } from './internals.js'; // VIS-194: three's private parts, checked at start-up

const HOOKS = Symbol('lightHooks');

/** The hooks on a material, in the order they run ([] if none). */
export const lightHooks = (material) => material?.[HOOKS] || [];
/** True when the material carries a hook with this key. */
export const hasLightHook = (material, key) => lightHooks(material).some((h) => h.key === key);

/**
 * Adds a lighting hook to a node material (MeshStandardNodeMaterial, MeshPhysicalNodeMaterial, ...). A second hook
 * with the same key replaces the first (same place in the order). Returns the material.
 */
export function addLightHook(material, hook) {
  if (!material?.isNodeMaterial) throw new Error(`addLightHook(${hook?.key}): not a node material (${material?.type})`);
  if (!material[HOOKS]) install(material);
  const list = material[HOOKS], i = list.findIndex((h) => h.key === hook.key);
  if (i >= 0) list[i] = hook; else list.push(hook);
  material.needsUpdate = true;
  return material;
}

/** Removes the hook with this key (no-op if absent). */
export function removeLightHook(material, key) {
  const list = material?.[HOOKS];
  const i = list ? list.findIndex((h) => h.key === key) : -1;
  if (i >= 0) { list.splice(i, 1); material.needsUpdate = true; }
  return material;
}

/**
 * The cap on a material's direct specular (materials.js capSpecular) as a value of the material, read from a uniform, so
 * a capped material and an uncapped one (NO_CAP: gpu/lightcast.js withCastLight gives every plain lit material the hook)
 * are one program: a first visit on an iPhone compiles it once (VIS-267). min(x, 1e30) is x. In the order of the hooks
 * before the cast light (which leaves the direct specular alone). A plain property, not userData: a clone has no hook,
 * and must not read as capped.
 */
export const NO_CAP = 1e30;
const capOf = materialReference('specMax', 'float');
export function specCap(material, value = material.specMax ?? NO_CAP) {
  material.specMax = value;
  if (hasLightHook(material, 'spec-cap')) return material;
  return addLightHook(material, { // WS-75: after all the direct lights went in
    key: 'spec-cap',
    lights: ({ reflectedLight, model }) => {
      const cap = vec3(capOf);
      reflectedLight.directSpecular.assign(min(reflectedLight.directSpecular, cap));
      if (model.clearcoatSpecularDirect) model.clearcoatSpecularDirect.assign(min(model.clearcoatSpecularDirect, cap));
    },
  });
}

function install(material) {
  const list = [];
  Object.defineProperty(material, HOOKS, { value: list, enumerable: false });
  const baseSetup = material.setupLightingModel; // the class's model (or another system's own override)
  const baseKey = material.customProgramCacheKey;
  // own, non-enumerable: getMaterialCacheKey() then keys the program by the hooks below, not by function sources
  Object.defineProperty(material, 'setupLightingModel', {
    configurable: true, writable: true, enumerable: false,
    value(builder) {
      const model = baseSetup.call(this, builder);
      if (!model || !list.length) return model;
      const start = model.start, finish = model.finish;
      const ctxOf = (b) => ({
        builder: b, material: this, model, reflectedLight: b.context.reflectedLight, outgoingLight: b.context.outgoingLight,
        ambientOcclusion: b.context.ambientOcclusion, diffuseColor,
      });
      model.start = function (b) { start.call(this, b); const c = ctxOf(b); for (const h of list) h.lights?.(c); };
      model.finish = function (b) { finish.call(this, b); const c = ctxOf(b); for (const h of list) h.finish?.(c); };
      return model;
    },
  });
  Object.defineProperty(material, 'customProgramCacheKey', {
    configurable: true, writable: true, enumerable: false,
    value() { return `${baseKey.call(this)}|lh:${list.map((h) => h.key).join(',')}`; },
  });
}

// ---- iGPU dark lights (begin): a light at intensity 0 adds exactly nothing, so its whole term is skipped.
// three r186 computes every scene light's BRDF on every lit pixel whatever its intensity: by day the moon and the fire
// lights (main.js, intensity 0 until dusk) were worked out on every pixel of the ground and the buildings. Each direct
// light that casts no shadow now runs inside an If on its own colour uniform (colour x intensity, updated per frame):
// a uniform branch, so the programs, their number and the warm start stay exactly as before (no light is added or
// removed at dusk) and a lit light gives the same sum. Shadow-casting lights (the sun) stay outside: their shadow
// node is part of the colour and is read by other passes. Called once by render.js before the first build.
export function skipDarkLights() {
  const LN = THREE.LightsNode.prototype;
  if (LN._darkLights || !usable('LightsNode.setupDirectLight')) return;
  LN._darkLights = true;
  const direct = LN.setupDirectLight;
  LN.setupDirectLight = function (builder, lightNode, data) {
    const L = lightNode?.light, c = lightNode?.colorNode;
    if (!L || L.castShadow || !c) return direct.call(this, builder, lightNode, data);
    If(max(c.r, max(c.g, c.b)).greaterThan(0.0), () => { direct.call(this, builder, lightNode, data); });
  };
}
// ---- iGPU dark lights (end)

// ---- WP5.1b (an iPhone's first visit): a light's shadow received as a value of the object (begin).
// three builds a light's shadow only into the programs of objects with receiveShadow (AnalyticLightNode.setup) and keys
// each program on it, so a receiver and a non-receiver of one surface were two programs (about 1 s each for a lit
// pipeline on an iPhone's first visit). A light whose shadow node answers byValue(builder) gets its shadow built for
// every object; that node reads the object's receiveShadow as a per-object uniform and skips all its work on 0
// (shadows.js SunShadowNode): one text for both, a non-receiver's light x exactly 1. three takes the light's colour in
// setupDirect, right after its own shadow step (internals.js checks that order). Called once by shadows.js.
export function shadowByValue() {
  const DL = THREE.DirectionalLightNode.prototype;
  if (DL._shadowByValue || !usable('AnalyticLightNode.setup')) return;
  DL._shadowByValue = true;
  const direct = DL.setupDirect;
  DL.setupDirect = function (builder) {
    if (this.light.castShadow && builder.object?.receiveShadow !== true && this.light.shadow?.shadowNode?.byValue?.(builder) === true) this.setupShadow(builder);
    return direct.call(this, builder);
  };
}
// ---- WP5.1b (end)
